import {
  EnvironmentOutlined,
  FundOutlined,
  PercentageOutlined,
  QuestionCircleOutlined,
  RadarChartOutlined,
  StockOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import type { FC, ReactNode } from "react";
import { ratioToStockPricePct } from "fin-web-components";
import type { StockDetailData } from "fin-web-components";

function formatScore(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatPrice(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : "—";
}

function formatChangePct(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function formatMarketValueYuan(yuan: number | null | undefined): string {
  if (yuan == null || !Number.isFinite(yuan)) {
    return "—";
  }
  const abs = Math.abs(yuan);
  const yi = 100_000_000;
  const wanYi = 10_000 * yi;
  if (abs >= wanYi) {
    return `${(yuan / wanYi).toFixed(2)}万亿`;
  }
  return `${(yuan / yi).toFixed(2)}亿`;
}

function formatDividendYield(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return `${value.toFixed(2)}%`;
}

function formatPeRatio(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return value.toFixed(2);
}

function quoteToneClass(value: number): string {
  if (value > 0) {
    return "lf-stock-detail-header__quote-row--up";
  }
  if (value < 0) {
    return "lf-stock-detail-header__quote-row--down";
  }
  return "lf-stock-detail-header__quote-row--flat";
}

interface ScoreItemProps {
  icon: ReactNode;
  label: string;
  score: number;
  tooltip: string;
}

const ScoreItem: FC<ScoreItemProps> = ({ icon, label, score, tooltip }) => (
  <span className="lf-stock-detail-header__score-item">
    <span className="lf-stock-detail-header__score-icon" aria-hidden="true">
      {icon}
    </span>
    <span className="lf-stock-detail-header__score-label">{label}</span>
    <span className="lf-stock-detail-header__score-value">{formatScore(score)}</span>
    <Tooltip title={tooltip} trigger={["hover", "click"]}>
      <span className="lf-stock-detail-header__score-help" role="button" tabIndex={0} aria-label={tooltip}>
        <QuestionCircleOutlined />
      </span>
    </Tooltip>
  </span>
);

interface MetricItemProps {
  icon: ReactNode;
  label: string;
  valueText: string;
  tooltip: string;
}

const MetricItem: FC<MetricItemProps> = ({ icon, label, valueText, tooltip }) => (
  <span className="lf-stock-detail-header__score-item">
    <span className="lf-stock-detail-header__score-icon" aria-hidden="true">
      {icon}
    </span>
    <span className="lf-stock-detail-header__score-label">{label}</span>
    <span className="lf-stock-detail-header__score-value">{valueText}</span>
    <Tooltip title={tooltip} trigger={["hover", "click"]}>
      <span className="lf-stock-detail-header__score-help" role="button" tabIndex={0} aria-label={tooltip}>
        <QuestionCircleOutlined />
      </span>
    </Tooltip>
  </span>
);

type Props = {
  detail: StockDetailData;
};

export const StockDetailHeader: FC<Props> = ({ detail }) => {
  const priceChangePct = ratioToStockPricePct(detail.priceChange ?? 0);

  return (
    <section className="lf-stock-detail-header">
      <div className={`lf-stock-detail-header__quote-row ${quoteToneClass(priceChangePct)}`}>
        <span className="lf-stock-detail-header__price">{formatPrice(detail.latestPrice ?? 0)}</span>
        <span className="lf-stock-detail-header__change">{formatChangePct(priceChangePct)}</span>
      </div>
      <div className="lf-stock-detail-header__scores">
        <ScoreItem
          icon={<RadarChartOutlined />}
          label="基本面分"
          score={detail.performanceScore}
          tooltip="基本面分：分数越高代表股票基本面越好"
        />
        <ScoreItem
          icon={<EnvironmentOutlined />}
          label="位置分"
          score={detail.positionScore}
          tooltip="位置分：分数越高，代表当前位置处于低位"
        />
        <MetricItem
          icon={<FundOutlined />}
          label="市值"
          valueText={formatMarketValueYuan(detail.marketValue)}
          tooltip="总市值"
        />
        <MetricItem
          icon={<PercentageOutlined />}
          label="股息率"
          valueText={formatDividendYield(detail.dividendYield)}
          tooltip="股息率"
        />
        <MetricItem
          icon={<StockOutlined />}
          label="市盈率"
          valueText={formatPeRatio(detail.peRatio)}
          tooltip="市盈率"
        />
      </div>
    </section>
  );
};
